import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken } from "./api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function usePrivileges() {
  const { user } = useAuth();
  if (!user || user.role !== "admin") return () => false;
  if (user.privileges === null || user.privileges === undefined) return () => true;
  const s = new Set(user.privileges);
  return (key) => s.has(key);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const loadMe = useCallback(() => {
    if (!localStorage.getItem("token")) return Promise.resolve(null);
    return api.me().then((u) => { setUser(u); return u; }).catch((err) => {
      if (err.status === 401 || err.status === 403) {
        setToken(null); // truly expired / invalid — clear and go to login
        setUser(null);
      }
      // Network errors: DON'T clear the token, keep user as-is
      // (on first load user will be null, but token survives for retry)
      return null;
    });
  }, []);

  useEffect(() => {
    if (localStorage.getItem("token")) {
      loadMe().finally(() => setReady(true));
    } else {
      setReady(true);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.login(email, password);
    setToken(res.access_token);
    const me = await api.me();
    setUser(me);
    return me;
  };

  const logout = () => { setToken(null); setUser(null); };

  // hasToken lets the app distinguish "no account" from "network error during refresh"
  const hasToken = !!localStorage.getItem("token");

  return (
    <AuthCtx.Provider value={{ user, ready, login, logout, hasToken, retryAuth: loadMe }}>
      {children}
    </AuthCtx.Provider>
  );
}
